import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import InfiniteScroll from '../infinite-scroll/InfiniteScroll'
import Placeholder from '../common/Placeholder'
import CoachActions from './CoachActions'
import CopyableValue from '../common/buttons/CopyableValue'

import AvatarPlaceholder from '../../assets/images/mock/default-profile-icon.png'
import classes from './CoachesTable.module.scss'

const STATUS_LABEL_KEYS = {
  active: 'coaches.table.statuses.active',
  invited: 'coaches.table.statuses.pendingInvitation',
  inactive: 'coaches.table.statuses.inactive',
}

const STATUS_CLASSES = {
  active: classes.statusActive,
  invited: classes.statusInvited,
  inactive: classes.statusInactive,
}

const CoachesTable = ({
  coachList,
  hasMore,
  onFetchMore,
}) => {
  const { t } = useTranslation()

  if (coachList.length === 0) {
    return (
      <div className='col-6 mx-auto'>
        <Placeholder
          title={t('coaches.table.noCoachesFound')}
          icon={'fa-user'}
          subtext1={t('coaches.table.noCoachesHelp')}
        />
      </div>
    )
  }

  return (
    <div className={classes.coachesTableContainer}>
      <InfiniteScroll
        dataLength={coachList.length}
        next={onFetchMore}
        hasMore={hasMore}
        scrollableTarget='tableContent'
        className={classes.tableContent}
      >
        <div id='tableContent' className={classes.coachesTable}>
          <table className={classNames('table table-borderless w-100 mb-0')}>
            <thead className={classes.tableHeader}>
              <tr className={classes.coachesTableHeader}>
                <th>{t('coaches.table.coach')}</th>
                <th>{t('common.field.email')}</th>
                <th>{t('coaches.common.community')}</th>
                <th>{t('common.field.phoneNumber')}</th>
                <th>{t('coaches.table.dateCreated')}</th>
                <th>{t('coaches.table.lastSignIn')}</th>
                <th>{t('common.field.status')}</th>
                <th>{t('coaches.table.idpId')}</th>
                <th>{t('coaches.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {coachList.map((coach) => (
                <tr key={coach.id} className={classNames(classes.coachRow)}>
                  <td>
                    <div className='d-flex align-items-center'>
                      <img
                        src={coach.avatar?.file_url || AvatarPlaceholder}
                        alt={`${coach.first_name} ${coach.last_name}`}
                        className={classes.coachAvatar}
                      />
                      <span className='font-weight-bold ml-2'>{coach.first_name} {coach.last_name}</span>
                    </div>
                  </td>
                  <td>{coach.email}</td>
                  <td>
                    {coach.communities?.[0] ? (
                      <Link to={`/communities/${coach.communities[0].id}`} target='_blank' rel='noopener noreferrer'>
                        {coach.communities[0].name}
                      </Link>
                    ) : 'N/A'}
                  </td>
                  <td>{coach.phone ? `${coach.phone_prefix ?? ''}${coach.phone}` : t('coaches.table.notAvailable')}</td>
                  <td>{coach.created_at ? moment(coach.created_at).format('MMM D, YYYY') : t('coaches.table.notAvailable')}</td>
                  <td>{coach.last_sign_in_at ? moment(coach.last_sign_in_at).format('MMM D, YYYY') : t('coaches.table.never')}</td>
                  <td>
                    <span className={classNames(classes.coachStatus, STATUS_CLASSES[coach.status])}>
                      {t(STATUS_LABEL_KEYS[coach.status] || STATUS_LABEL_KEYS.active)}
                    </span>
                  </td>
                  <td>
                    {coach.oauth_external_id && (
                      <CopyableValue value={coach.oauth_external_id} label={t('coaches.table.idpId')} />
                    )}
                  </td>
                  <td className={`${classes.coachCell} ${classes.coachActionsCell}`}>
                    <CoachActions coach={coach} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </InfiniteScroll>
    </div>
  )
}

CoachesTable.propTypes = {
  coachList: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasMore: PropTypes.bool.isRequired,
  onFetchMore: PropTypes.func.isRequired,
}

export default CoachesTable
